import type {
  AvailabilityHoldState,
  ComparableField,
  ComparableFieldReason,
  FieldAuthority,
  FieldDiffState,
  FrozenMappingRefusalReason,
  FreshnessState,
  IdentifiedUnit,
  IdentityOrigin,
  InventoryDiscrepancyReason,
  InventoryGroupReason,
  InventoryMovementKind,
  InventoryReconciliationState,
  InventoryReviewState,
  InventoryRowGrain,
  InventoryStockState,
  StockMismatchReason,
  LocalStockState,
  MarketplaceListingReason,
  MarketplacePresenceState,
  MatchBucket,
  MatchReadUnavailableReason,
  MatchReason,
  MatchRule,
  MissingTaxonomyFacet,
  ModelGroupingReason,
  OnecCodeParseState,
  ProductMediaKind,
  PublishedProductStatus,
  ProposalDecision,
  ProposalState,
  ProviderConnectionState,
  PublicationGroupReason,
  PublicationState,
  SourceProjectionReason,
  WarehouseFactReason,
  WarehouseIdentityReason,
  WarehouseProductTypeCode,
  WarehouseSource,
  WarehouseStatusScope,
} from '@/lib/api/schemas/inventory.schema'

/**
 * One Russian sentence per member of a published set. Every table below is an
 * exhaustive `Record` over a type derived from `forge/openapi.json`, so a member
 * added to the contract fails the build here rather than rendering as a blank
 * cell or as the engineering token itself.
 */
// Section headings only. Every sentence explaining why a tile has no bytes is
// server-owned and rendered verbatim, so no reason code is mapped to Russian
// twice.
export const productMediaKindLabels: Record<ProductMediaKind, string> = {
  image: 'Фото',
  video: 'Видео',
  instruction: 'Инструкции',
  unknown: 'Неизвестный тип файла',
}

export const discrepancyLabels: Record<InventoryDiscrepancyReason, string> = {
  missing_binding: 'Нет привязки',
  conflicting_binding: 'Конфликт привязок',
  price_divergence: 'Расхождение цены',
  stock_divergence: 'Расхождение остатка',
  stale_observation: 'Устаревшие данные',
  recent_movement: 'Недавнее движение',
  synchronization_failure: 'Ошибка синхронизации',
}

export const stockMismatchReasonLabels: Record<StockMismatchReason, string> = {
  stock_divergence: 'Количества расходятся',
  marketplace_reported_zero: 'Площадка сообщила ноль',
  marketplace_never_reported: 'Площадка ещё не сообщала остаток',
  marketplace_quantity_unavailable: 'Площадка сообщила остаток без числа',
  onec_observation_absent: '1С не сообщила остаток',
}

export const publicationStateLabels: Record<PublicationState, string> = {
  submitted: 'Отправлена',
  live: 'Опубликована',
  drifted: 'Есть расхождения',
  paused: 'Приостановлена',
  awaiting_review: 'Ожидает проверки',
  rejected: 'Отклонена',
  archived: 'В архиве',
  unknown: '—',
}

export const inventoryAlertLabels = {
  multipleLiveListings: 'Несколько активных публикаций',
} as const

export const reconciliationLabels: Record<InventoryReconciliationState, string> = {
  confirmed: 'Сверено с 1С',
  awaiting_1c: 'Ожидает 1С',
  stale: 'Требуется новое подтверждение 1С',
  unavailable: 'Данные недоступны',
  requires_review: 'Требует проверки',
}

export const inventoryRowGrainLabels: Record<InventoryRowGrain, string> = {
  piece: 'Изделие',
  unresolved_subject: 'Запись без источника происхождения · не изделие',
  merge_survived: 'Свёрнута при объединении · решение за владельцем',
}

export const inventoryReviewStateLabels: Record<InventoryReviewState, string> = {
  ready: 'Готово к просмотру',
  needs_review: 'Требует проверки',
  conflict: 'Конфликт источников',
  unavailable: 'Покрытие ограничено',
}

export const inventoryStockStateLabels: Record<InventoryStockState, string> = {
  confirmed: 'Подтверждено 1С',
  explicit_zero: 'Явный нулевой остаток',
  unknown: 'Остаток неизвестен',
  stale: 'Остаток устарел',
  unavailable: 'Остаток недоступен',
}

/**
 * Product status is a business state, never a quantity or a snapshot. The set
 * is the server's `publishedStatus` exactly, member for member: the column
 * renders a label for what the server said and the filter asks for the same
 * member by name, so the two cannot disagree.
 */
export type WarehouseProductStatus = PublishedProductStatus

export const warehouseProductStatusLabels: Record<WarehouseProductStatus, string> = {
  in_stock: 'В наличии',
  reserved: 'Зарезервировано',
  sold: 'Продано',
  withdrawn: 'Выведено из оборота',
  // A register aggregate reporting none on hand. Not a sale and not a
  // withdrawal, and it kept being read as one while it shared their name.
  out_of_stock: 'Нет на остатке',
  in_transit: 'В доставке',
  being_returned: 'Возвращается',
  delivery_issue: 'Проблема доставки',
  unknown: 'Состояние неизвестно',
  conflict: 'Конфликт сведений',
}

/** The status filter's own vocabulary: the statuses plus the clearing member. */
export const warehouseStatusScopeLabels: Record<WarehouseStatusScope, string> = {
  all: 'Все статусы',
  ...warehouseProductStatusLabels,
}

export const freshnessLabels: Record<FreshnessState, string> = {
  fresh: 'Свежие данные',
  stale: 'Данные устарели',
  unavailable: 'Нет наблюдения',
  failed: 'Ошибка синхронизации',
}

export const holdStateLabels: Record<AvailabilityHoldState, string> = {
  awaiting_1c: 'Ожидает 1С',
  confirmed: 'Подтверждено',
  requires_review: 'Требует проверки',
}

export const movementLabels: Record<InventoryMovementKind, string> = {
  marketplace_order: 'Заказ маркетплейса',
  onec_offline_sale: 'Офлайн-продажа 1С',
  onec_inventory_set: 'Подтверждение остатка 1С',
  onec_reservation: 'Резерв 1С',
}

export const unitReasonLabels: Record<string, string> = {
  'unit.source_account_not_recorded': 'Аккаунт источника не записан',
  'unit.source_time_not_recorded': 'Время факта у источника не записано',
  'unit.source_identity_not_recorded': 'Источник не указал, о каком изделии этот факт',
  'unit.warehouse_receipt_not_recorded': 'Приёмка на нашем складе не подтверждена',
  'unit.condition_clearance_not_recorded': 'Состояние изделия после приёмки не проверено',
  'unit.demand_unit_relation_not_recorded': 'Связь спроса с конкретным изделием не записана',
  'unit.aggregate_observation_not_unit_identity': 'Агрегированный остаток не определяет отдельные изделия',
  'unit.parcel_not_unit_identity': 'Посылка не сопоставлена с отдельным изделием',
  'unit.parcel_source_relation_not_recorded': 'Связь посылки с источником заказа не записана',
  'unit.parcel_source_relation_contested': 'Связь посылки с заказом противоречива',
  'unit.parcel_account_binding_not_recorded': 'Привязка источника заказа к аккаунту реестра не определена',
  'unit.demand_reason_unrecognized': 'Причина спроса источника требует проверки',
  'unit.marking_not_recorded_in_source': 'Источник не сообщил маркировку этого изделия',
  'unit.listing_target_grain_not_decided': 'Не решено, что продаёт источник: изделие, пул или набор',
  'unit.receipt_attestation_policy_unresolved': 'Порядок подтверждения приёмки не утверждён',
  'unit.carrier_arrival_is_not_warehouse_receipt': 'Прибытие посылки в пункт выдачи не является приёмкой на склад',
  'unit.commercial_event_is_not_warehouse_receipt': 'Коммерческое событие, включая отмену заказа, не является приёмкой на склад',
  'unit.aggregate_balance_is_not_a_piece': 'Агрегированный остаток не является физическим изделием',
  'unit.condition_assessment_policy_unresolved': 'Порядок проверки состояния не утверждён',
  'unit.carrier_state_is_not_condition_clearance': 'Статус перевозчика не является проверкой состояния изделия',
  'unit.protection_scope_binding_unresolved': 'Защиту нельзя отнести к изделию: не задана связь аккаунта реестра с источником заказа и её кратность; это не значит, что ограничений нет',
  'unit.marking_absence_restriction_unresolved': 'Не решено, ограничивает ли отсутствие маркировки продажу',
}

// Closed enum rendering, not classification: every member the server can send
// has exactly one sentence here, and the browser picks none of them itself.
export const unitReceiptStateLabels = {
  authorized_warehouse_receipt: 'Приёмка на нашем складе подтверждена',
  not_established: 'Приёмка на нашем складе не подтверждена',
} satisfies Record<IdentifiedUnit['receiptState'], string>

export const unitMarkingLabels = {
  uin_recorded: 'УИН записан',
  not_recorded: 'Маркировка не записана',
} as const

export const unitProvenanceLabels = {
  reviewed_enrichment_decision: 'Проверенное решение обогащения',
  source_import_evidence: 'Импорт источника, без проверки человеком',
} as const

export const unitEligibilityLabels = {
  eligible: 'Доступно к продаже',
  ineligible: 'Не доступно к продаже',
  undecidable: 'Решение невозможно: нет утверждённой политики',
} as const

// Every gate is open, so this map is not a lookup that may miss: it is the list
// of owner decisions the page has to be able to name in Russian.
export const unitPolicyGateLabels = {
  'unit.policy.receipt_condition_actor_and_correction_evidence': 'Кто подтверждает приёмку и состояние и как фиксируется исправление',
  'unit.policy.marking_absence_restrictions': 'Ограничения для изделий без маркировки',
  'unit.policy.onec_information_base_account_continuity': 'Преемственность информационных баз и аккаунтов 1С',
  'unit.policy.provider_account_sales_source_mapping': 'Какая связь и кратность соединяют аккаунт реестра с источником заказа',
  'unit.policy.listing_target_grain': 'Что продаёт источник: изделие, пул или набор',
} as const

export const unresolvedPopulationLabels = {
  demand: 'Коммерческий спрос без распределения',
  parcel: 'Посылка без связи с изделием',
  aggregate_observation: 'Агрегированное наблюдение остатка',
} as const

export const populationUnavailableReasonLabels = {
  'unit.parcel_account_mapping_unavailable': 'Посылки нельзя отнести к выбранному аккаунту: проверенная связь аккаунта реестра с источником заказа не заведена',
  'unit.parcel_variant_relation_unavailable': 'Посылки нельзя отнести к выбранному изделию: связь посылки с товаром не записана',
} as const

export const unitLifecycleLabels = {
  in_stock: 'В реестре склада',
  reserved: 'Зарезервировано',
  sold: 'Продано',
  withdrawn: 'Выведено из оборота',
} as const

export const presenceLabels: Record<MarketplacePresenceState, string> = {
  present: 'Размещено',
  absent: 'Не размещено',
  unknown: 'Нет наблюдения',
}

export const connectionLabels: Record<ProviderConnectionState, string> = {
  connected: 'Подключено',
  not_connected: 'Не подключено',
  no_observation: 'Нет наблюдения',
}

export const diffStateLabels: Record<FieldDiffState, string> = {
  equal: 'Совпадает',
  different: 'Отличается',
  missingCanonical: 'Нет канонического значения',
  missingSource: 'Нет значения источника',
  stale: 'Наблюдение устарело',
  unsupported: 'Поле не поддерживается площадкой',
  incompatible: 'Значения несопоставимы',
  conflict: 'Конфликт владельцев поля',
}

export const authorityLabels: Record<FieldAuthority, string> = {
  canonical: 'Каноническая запись',
  onec_frozen_registry: 'Зафиксированный реестр 1С',
  provider_account: 'Аккаунт площадки',
  operator_review: 'Решение оператора',
}

export const comparableFieldLabels: Record<ComparableField, string> = {
  title: 'Название',
  sellerSku: 'SKU продавца',
  price: 'Цена',
  currency: 'Валюта',
  stock: 'Остаток',
  listingStatus: 'Состояние публикации',
  material: 'Материал',
  fineness: 'Проба',
  productType: 'Тип изделия',
  shape: 'Форма',
  techSize: 'Размер',
  netWeightGrams: 'Вес изделия, г',
}

export const matchRuleLabels: Record<MatchRule, string> = {
  exact_physical_unit_uin: 'Точное совпадение по УИН',
  reviewed_alias: 'Проверенный псевдоним',
  unique_article_size: 'Производитель, артикул и размер совпадают однозначно',
  unique_article_size_without_manufacturer: 'Артикул и размер совпадают однозначно, производитель совместим или отсутствует',
}

export const matchBucketLabels: Record<MatchBucket, string> = {
  matched: 'Сопоставлено',
  unmatched: 'Без сопоставления',
  conflict: 'Конфликт',
  manual_review: 'Требует разбора',
  unavailable: 'Данных недостаточно',
}

export const matchReadUnavailableReasonLabels: Record<MatchReadUnavailableReason, string> = {
  'match.unknown_persisted_vocabulary': 'В сохранённом сопоставлении найдено неизвестное значение',
}

export const matchReasonLabels: Record<MatchReason, string> = {
  exact_physical_unit_uin: 'Точное совпадение по УИН физической единицы',
  reviewed_alias: 'Проверенный псевдоним',
  unique_article_size: 'Производитель, артикул и размер совпадают однозначно',
  unique_article_size_without_manufacturer: 'Артикул и размер совпадают однозначно, производитель совместим или отсутствует',
  canonical_source_of_record: 'Каноническая запись 1С',
  no_source_identity_evidence: 'У источника нет доказательств идентичности',
  offer_id_missing: 'У наблюдения нет идентификатора предложения',
  no_reviewed_mapping: 'Нет проверенной привязки',
  refused_marketplace_mapping: 'Привязка маркетплейса отклонена',
  malformed_onec_code: 'Некорректный код 1С «артикул/размер»',
  duplicate_tuple: 'Дубль пары «производитель, артикул, размер» в источнике',
  manufacturer_disagreement: 'Производители различаются',
  duplicate_alias_value: 'Псевдоним встречается более одного раза',
  source_local_collision: 'Коллизия ключей внутри источника',
  canonical_product_mismatch: 'Канонический товар не совпадает с товаром источника',
  contradictory_material: 'Материал противоречит другому владельцу поля',
  contradictory_fineness: 'Проба противоречит другому владельцу поля',
  contradictory_shape: 'Форма противоречит другому владельцу поля',
  contradictory_size: 'Размер противоречит другому владельцу поля',
  contradictory_net_weight: 'Вес изделия противоречит другому владельцу поля',
  ambiguous_size: 'Размер источника не приводится к одному значению',
  title_only_match: 'Совпадение только по названию',
  fuzzy_sku_match: 'Нечёткое совпадение SKU',
  ambiguous_variant_match: 'Подходит более одного канонического товара',
  unknown_source_variant: 'Товар источника неизвестен',
  no_provider_qualified_alias: 'Для этой площадки привязка артикула не заведена',
  source_snapshot_absent: 'Снимок источника не получен',
  package_weight_only: 'Есть только вес упаковки',
  material_evidence_absent: 'Нет доказательств материала',
  fineness_evidence_absent: 'Нет доказательств пробы',
  net_weight_evidence_absent: 'Нет доказательств веса изделия',
}

// The frozen 1C registry refuses a marketplace mapping with its own review
// vocabulary, which is NOT `MatchReason`. Rendering these through the matcher
// table put an engineering token on screen for every refused mapping.
export const frozenMappingRefusalLabels: Record<FrozenMappingRefusalReason, string> = {
  exact_source_identity: 'Точное совпадение идентификатора источника',
  reviewed_crosswalk: 'Проверенное сопоставление',
  unknown_variant: 'Товар 1С неизвестен',
  duplicate_seller_sku: 'SKU продавца встречается более одного раза',
  duplicate_order_item: 'Строка заказа встречается более одного раза',
  title_only_forbidden: 'Совпадение только по названию не принимается',
  name_only_forbidden: 'Совпадение только по наименованию не принимается',
  missing_marketplace_mapping: 'Сопоставление с площадкой не заведено',
  'frozen_mapping.reason_not_published': 'Причина отказа требует проверки',
}

export const proposalDecisionLabels: Record<ProposalDecision, string> = {
  acceptSourceIntoCanonical: 'Принять значение источника в каноническое',
  keepCanonical: 'Сохранить каноническое значение',
  compileCanonicalToTargets: 'Собрать желаемое состояние для площадок',
  ignoreForTarget: 'Не применять для этой площадки',
  manualReview: 'Требует решения оператора',
}

export const proposalStateLabels: Record<ProposalState, string> = {
  draft: 'Черновик',
  reviewed: 'Проверено',
  applied: 'Применено',
  rejected: 'Отклонено',
}

export const identityOriginLabels: Record<IdentityOrigin, string> = {
  existing_resolution: 'Сопоставлен с существующим каноническим товаром',
  new_candidate: 'Новый кандидат',
}

export const onecParseStateLabels: Record<OnecCodeParseState, string> = {
  parsed: 'Код 1С разобран',
  malformed: 'Код 1С некорректен — артикул не выведен',
  unavailable: 'Кода 1С нет',
}

export const localStockLabels: Record<LocalStockState, string> = {
  in_stock: 'Есть на складе',
  out_of_stock: 'Нет на складе',
  unknown: 'Остаток неизвестен',
}

export const missingTaxonomyLabels: Record<MissingTaxonomyFacet, string> = {
  any: 'Любой из четырёх',
  productType: 'Тип изделия',
  shape: 'Форма',
  material: 'Материал',
  fineness: 'Проба',
}

export const inventoryGroupReasonLabels: Record<InventoryGroupReason, string> = {
  'group.article_not_recorded': 'Артикул не зафиксирован',
  'group.tech_size_not_recorded': 'Размер не зафиксирован',
  'group.stock_observation_absent': 'Нет подтверждённого остатка по 1С',
  'group.quantity_not_reported': '1С передала остаток без количества',
  'group.unit_weight_not_recorded': 'Вес изделий не зафиксирован',
  'group.no_multi_member_confirmed_model': 'Нет подтверждённой модели из нескольких артикулов',
  'group.frozen_registry_row_not_grouped': 'Строка реестра 1С не отнесена к группе',
  'group.key_not_recognized': 'Группа не распознана сервером',
  'group.reason_not_published': 'Причина недоступности требует проверки',
}

/**
 * The published unavailable-reason families, one table each. The server
 * normalizes anything it cannot publish - a retired member, a raw import token,
 * a value from a newer contract - onto the family's `*_reason_not_published`
 * member, so an operator reads a sentence rather than a token in every case.
 *
 * A code that appears in more than one family carries the same sentence in all
 * of them; `warehouse-labels.test.ts` asserts that, because two spellings of
 * one absence is how one state ends up described two ways on two screens.
 */
export const warehouseFactReasonLabels: Record<WarehouseFactReason, string> = {
  'product_type.not_recorded': 'Тип изделия не зафиксирован',
  'shape.not_recorded': 'Форма не зафиксирована',
  'material.not_recorded': 'Материал не зафиксирован',
  'material.unsupported_source_value': 'Металл источника не входит в наш справочник',
  'model.material_not_evidenced': 'Материал модели не подтверждён при импорте',
  'fineness.not_recorded': 'Проба не зафиксирована',
  'model.fineness_not_evidenced': 'Проба модели не подтверждена при импорте',
  'unit.weight_not_recorded': 'Авторитетный вес изделия не зафиксирован',
  'unit.marking_not_recorded_in_source': 'Источник не сообщил маркировку этого изделия',
  'state.not_recorded': '1С не записала состояние этого изделия',
  'state.onec_registers_silent': '1С не отражает это изделие ни в остатках, ни в продажах, ни в резервах',
  'state.onec_sold_again_without_return': '1С записала повторную продажу этого изделия без возврата между продажами',
  'state.onec_reserved_not_on_hand': '1С резервирует это изделие, но в остатке его нет',
  'arrival.not_recorded': '1С не зафиксировала поступление этого изделия',
  'weight.authoritative_net_missing': 'Источник не передал чистый вес украшения',
  'weight.package_only_forbidden': 'Источник передал только вес упаковки: он не заменяет вес изделия',
  'metal_weight.not_extracted': 'Вес металла ещё не получен из 1С',
  'metal_weight.not_recorded': '1С не указала вес металла',
  'metal_weight.invalid_in_onec': 'Вес металла в 1С записан с ошибкой',
  'insert_weight.not_extracted': 'Вес вставок ещё не получен из 1С',
  'insert_weight.none_recorded': '1С не указала вес вставок',
  'insert_weight.invalid_in_onec': 'Вес вставок в 1С записан с ошибкой',
  'insert_carats.not_extracted': 'Вес вставок в каратах ещё не получен из 1С',
  'insert_carats.not_stated': '1С не указала вес вставок в каратах',
  'insert_carats.spec_truncated': 'Описание вставок в 1С обрезано, карат не сложить',
  'insert_carats.invalid_in_onec': 'Вес вставок в каратах в 1С записан с ошибкой',
  'insert_stones.not_extracted': 'Состав вставок ещё не получен из 1С',
  'insert_stones.none_recorded': 'В 1С у изделия нет вставок',
  'insert_stones.total_only': '1С указала только общий вес вставок, без состава',
  'corrected_metal.composition_not_extracted': 'Состав изделия ещё не получен из 1С',
  'corrected_metal.stone_spec_truncated': 'Описание вставок в 1С обрезано; исправленный вес металла не рассчитан',
  'corrected_metal.stone_kind_not_recorded': '1С указала вес вставок без их вида; исправленный вес металла не рассчитан',
  'corrected_metal.stone_kind_ambiguous': 'Вид или происхождение вставки неоднозначны; исправленный вес металла не рассчитан',
  'corrected_metal.precious_weight_not_recorded': '1С не указала вес природного драгоценного камня',
  'corrected_metal.precious_weight_invalid': 'Вес природного драгоценного камня в 1С записан с ошибкой',
  'fact.reason_not_published': 'Причина недоступности требует проверки',
}

export const warehouseIdentityReasonLabels: Record<WarehouseIdentityReason, string> = {
  'manufacturer.not_recorded': 'Производитель не зафиксирован',
  'brand.not_recorded': 'Бренд не задан оператором',
  'brand.not_captured': 'Бренд источником не передан',
  'brand.conflicting_sources': 'Источники называют разные бренды',
  'article.not_recorded': 'Артикул не зафиксирован',
  'tech_size.not_recorded': 'Размер не зафиксирован',
  // The same sentence the comparable-field family already gives this code. One
  // code, one sentence: two spellings of one absence is how one state ends up
  // described two ways on two screens.
  'title.not_recorded': 'Название не зафиксировано',
  'description.not_recorded': 'Описание не зафиксировано',
  'unit.marking_not_recorded_in_source': 'Источник не сообщил маркировку этого изделия',
  // Two absences, two sentences. The first is open for a person to settle; the
  // second names whose decision is holding it, so it cannot read as neglected.
  'identity.source_identity_not_resolved': 'Происхождение не установлено: это не изделие',
  'identity.previously_merged': 'Свёрнута при объединении: решение за владельцем',
  'identity.reason_not_published': 'Причина недоступности требует проверки',
}

export const modelGroupingReasonLabels: Record<ModelGroupingReason, string> = {
  'model.grouping_not_reviewed': 'Модель не сгруппирована',
  'model.reason_not_published': 'Причина недоступности требует проверки',
}

// Why one provider account's publication group carries no readable state. The
// state itself is rendered from `PublicationState`; this table answers only the
// case where the state could not be read at all, so an unreadable state never
// borrows the appearance of a confirmed one.
export const publicationGroupReasonLabels: Record<PublicationGroupReason, string> = {
  'publication.state_not_evidenced': 'Состояние публикации на площадке не подтверждено',
  'publication_group.reason_not_published': 'Причина недоступности требует проверки',
}

export const marketplaceListingReasonLabels: Record<MarketplaceListingReason, string> = {
  'price.not_provided': 'Цена источником не передана',
  'stock.provider_observation_absent': 'Нет наблюдения остатка на площадке',
  'listing.observation_absent': 'Наблюдения по публикации нет',
  'provider.account_not_configured': 'Аккаунт площадки не настроен',
  'listing.reason_not_published': 'Причина недоступности требует проверки',
}

export const sourceProjectionReasonLabels: Record<SourceProjectionReason, string> = {
  'inventory.observation_absent': 'Наблюдения нет',
  'inventory.observation_stale': 'Наблюдение устарело',
  'inventory.sync_failed': 'Синхронизация завершилась ошибкой',
  'inventory.snapshot_incomplete': 'Снимок источника неполный',
  'inventory.price_unavailable': 'Цена недоступна',
  'price.not_provided': 'Цена источником не передана',
  'price.not_recorded': 'Цена в реестре не зафиксирована',
  'inventory.stock_unavailable': 'Остаток недоступен',
  'stock.publication_evidence_absent': 'Нет подтверждения остатка от источника',
  'stock.product_movement_unavailable': 'Движение изделий не прочитано, остаток не посчитан',
  'stock.product_movement_conflict': 'Два факта о движении с одним временем расходятся',
  'inventory.reason_not_published': 'Причина недоступности требует проверки',
}

export const comparableFieldReasonLabels: Record<ComparableFieldReason, string> = {
  'title.not_recorded': 'Название не зафиксировано',
  'seller_sku.not_recorded': 'SKU продавца не зафиксирован',
  'price.not_provided': 'Цена источником не передана',
  'currency.not_recorded': 'Валюта не зафиксирована',
  'stock.provider_observation_absent': 'Нет наблюдения остатка на площадке',
  'listing_status.not_recorded': 'Состояние публикации не зафиксировано',
  'material.not_recorded': 'Материал не зафиксирован',
  'fineness.not_recorded': 'Проба не зафиксирована',
  'product_type.not_recorded': 'Тип изделия не зафиксирован',
  'shape.not_recorded': 'Форма не зафиксирована',
  'tech_size.not_recorded': 'Размер не зафиксирован',
  'unit.weight_not_recorded': 'Авторитетный вес изделия не зафиксирован',
  'field.reason_not_published': 'Причина недоступности требует проверки',
}

// Why a row's photo cell is empty. The sentences are the server's own, copied
// verbatim from `forge.inventory.media.PRODUCT_MEDIA_UNAVAILABLE`, because a
// list row carries the reason code without the sentence and one absence must
// not read two ways between the row and the card that publishes both.
export const productMediaReasonLabels: Record<string, string> = {
  'product_media.not_recorded': 'Фото и видео для этого изделия не записаны.',
  'product_media.download_pending': 'Файл ещё не загружен в наше хранилище.',
  'product_media.download_failed': 'Загрузить файл из источника не удалось.',
  'product_media.asset_unresolved': 'Файл отмечен загруженным, но объект хранения не найден.',
  'product_media.status_unknown': 'Состояние файла записано значением, которого нет в справочнике.',
}

// The families `unavailableReasonText` reads. The frozen refusal set is not
// among them: it is rendered by its own lookup on the evidence card, and
// folding it in would let a refusal code answer for an unavailable value.
export const publishedReasonLabelTables: readonly Record<string, string>[] = [
  warehouseFactReasonLabels,
  warehouseIdentityReasonLabels,
  modelGroupingReasonLabels,
  publicationGroupReasonLabels,
  marketplaceListingReasonLabels,
  sourceProjectionReasonLabels,
  comparableFieldReasonLabels,
  inventoryGroupReasonLabels,
  productMediaReasonLabels,
]

// Display reasons this adapter emits where the payload models no reason field.
// They are deliberately a separate table so the two families cannot blur.
export const displayReasonLabels: Record<string, string> = {
  'presence.not_observed': 'Присутствие не наблюдалось',
  'identity.article_absent': 'Артикул не зафиксирован',
  'identity.size_absent': 'Размер не зафиксирован',
  'identity.raw_source_key_absent': 'Исходный код 1С не зафиксирован',
  'listing.not_connected': 'Канал не подключён',
  'listing.offer_id_absent': 'Идентификатора предложения нет',
  'inventory.activity_not_recorded': 'Деловое движение для этого товара не записано',
  'inventory.product_state_unavailable': 'Состояние конкретного изделия не опубликовано',
  cost_not_known: 'Закупочная стоимость 1С не известна',
  weight_not_measured: 'Вес изделия не измерен',
  tariffs_unavailable: 'Тарифы Маркета недоступны',
  bids_unavailable: 'Ставка буста недоступна',
  price_not_known: 'Цена Маркета не собрана',
  price_not_collected: 'Снимок цены Маркета ещё не собран',
  showcase_price_not_collected: 'Цена для покупателя ещё не собрана',
  seller_promo_factor_invalid: 'Не удалось учесть скидку продавца в действующей акции',
  delivery_not_collected: 'Тариф доставки покупателю не собран',
  no_orders_yet: 'Заказов ещё не было',
  not_in_promo: 'Участия в активной акции нет',
  bids_not_set: 'Не участвует в бусте',
  boost_not_enabled: 'Буст не включён',
  cost_not_positive: 'Закупочная стоимость 1С не положительна',
  economics_not_collected: 'Экономика Маркета не собрана',
  earnings_ymarket_only: 'Заработок считается только для Яндекс Маркета',
  recommendation_ymarket_only: 'Рекомендация политики есть только для Яндекс Маркета',
  current_price_missing: 'Нет текущей цены на площадке',
  not_on_marketplace: 'Нет на площадке',
  platform_not_connected: 'Площадка не подключена',
  offer_not_resolved: 'Предложение площадки не определено',
  offer_ambiguous: 'Предложение площадки неоднозначно',
  price_not_observed: 'Текущая цена не наблюдалась',
  ymarket_economics_unavailable: 'Экономика Маркета не собрана',
  forge_price_not_set: 'Наша цена для Маркета ещё не задана',
  ymarket_offer_missing: 'Предложения нет на Маркете',
  ymarket_prices_never_collected: 'Цены Маркета ещё не собирались',
  ymarket_discount_base_absent: 'Зачёркнутой цены на Маркете нет',
  ymarket_minimum_absent: 'Минимум для акций на Маркете не задан',
  net_economics_not_collected: 'На руки ещё не посчитано — нажмите «Собрать цены сейчас» на «Площадках»',
  not_exposed_by_api: 'Маркет не отдаёт это значение',
  stone_piece: 'Изделие со вставкой — цель ₽/г к нему не применяется',
  insert_status_unavailable: 'Неизвестно, есть ли у изделия вставка',
  target_not_positive: 'Цель ₽/г должна быть больше нуля',
  discount_markup_invalid: 'Наценка зачёркнутой цены задана неверно',
  commission_rate_invalid: 'Комиссия Маркета получилась неверной',
  'state.legacy_withdrawn_not_recorded': 'Изделие было списано в старом учёте; нынешнее состояние не записано',
  // The storefront price read sends these as ready Russian words, not codes.
  'Не подключено': 'Площадка не подключена',
  'Нет наблюдения': 'Площадка ещё не прислала данные об изделии',
  'Нет на площадке': 'Изделия нет на площадке',
  // Economics inputs: why a cost, tariff, bid or buyer price stayed empty.
  cost_not_recorded: 'В 1С у партии не заполнена стоимость',
  cost_invalid: 'Стоимость партии в 1С не является суммой',
  cost_conflicting_rows: 'Партии 1С называют разную стоимость изделия',
  receipt_provenance_missing: 'У стоимости в 1С нет документа или даты поступления',
  receipt_not_found: 'В 1С нет документа поступления с этим УИН',
  receipt_quantity_not_one: 'Поступление 1С записало этот УИН не одной штукой',
  receipt_cost_is_retail_price: 'Перенос остатков 2020 года записал в 1С розничную цену вместо закупочной',
  cost_not_synced: 'Себестоимость из 1С ещё не загружена для этого изделия',
  product_not_linked: 'Предложение не связано с изделием',
  uin_not_recorded: 'У партии 1С нет УИН',
  uin_not_in_forge: 'УИН из 1С нет среди изделий',
  price_missing: 'Нет цены Маркета для расчёта тарифа',
  product_type_missing: 'Вид изделия не указан',
  package_dimensions_missing: 'Не заданы габариты упаковки для этого вида изделия',
  package_dimensions_missing_all_sources: 'Габаритов упаковки нет ни на Маркете, ни на Wildberries, ни в настройках вида изделия',
  package_dimensions_source_unavailable: 'Не удалось прочитать габариты упаковки с площадки',
  market_category_missing: 'Категория Маркета не известна',
  request_budget_exhausted: 'Лимит запросов к Маркету на этот сбор исчерпан',
  tariff_rate_limited: 'Маркет ограничил частоту запросов тарифов',
  tariff_access_denied: 'Токен Маркета не имеет доступа к расчёту тарифов',
  tariff_request_refused: 'Маркет отклонил запрос расчёта тарифов',
  tariff_provider_error: 'Ошибка на стороне Маркета при расчёте тарифов',
  tariff_transport_failed: 'Маркет не ответил на запрос тарифов',
  tariff_response_rejected: 'Ответ Маркета о тарифах не прошёл проверку',
  tariff_response_mismatch: 'Ответ Маркета о тарифах не совпал с запросом',
  tariff_currency_mismatch: 'Маркет вернул тариф не в рублях',
  tariff_calculator_unavailable: 'Расчёт тарифов недоступен',
  tariffs_not_collected: 'Тарифы Маркета не собирались',
  commission_not_collected: 'Комиссия Маркета не собрана',
  not_on_display: 'Маркет не показывает изделие на витрине',
  showcase_not_collected: 'Цена на витрине ещё не собрана',
  showcase_not_collected_at_order: 'Отчёт «Цены» на момент заказа не собран',
  showcase_value_unrecognized: 'Маркет прислал нераспознанную цену на витрине',
  tariff_input_unavailable: 'Тарифы не считались: сборщик ещё не обновлён',
  bids_rate_limited: 'Маркет ограничил частоту запросов ставок',
  bids_access_denied: 'Токен Маркета не имеет доступа к ставкам буста',
  bids_request_refused: 'Маркет отклонил запрос ставок буста',
  bids_provider_error: 'Ошибка на стороне Маркета при чтении ставок',
  bids_transport_failed: 'Маркет не ответил на запрос ставок',
  bids_response_rejected: 'Ответ Маркета о ставках не прошёл проверку',
  bids_page_limit_exceeded: 'Ставок больше, чем читается за один сбор',
  buyer_price_not_stored: 'Цена покупателя не сохранена в заказе',
  formula_stored_invalid: 'Сохранённая формула витрины больше не проходит проверку',
  formula_division_by_zero: 'В формуле витрины деление на ноль',
  formula_number_too_large: 'Формула витрины дала слишком большое число',
  formula_invalid_operation: 'Формула витрины не считается для этого изделия',
  formula_type_mismatch: 'Формула витрины получила значение не того типа',
  formula_result_not_number: 'Формула витрины не дала числа',
  formula_variable_not_provided: 'Формуле витрины не передано значение',
  formula_not_monotonic: 'Формула «На руки» не растёт вместе с ценой',
  formula_target_unreachable: 'Цель на руки недостижима по формулам',
  report_has_no_pay_price: 'Отчёт «Цены» не дал отдельной цены с Яндекс Пэй',
  product_type_unknown: 'Тип изделия не указан',
  minimum_for_promos_not_set: 'Минимум для акций не задан',
  threshold_not_collected: 'Порог из отчёта «Цены» не собран',
  boost_not_in_campaign: 'Изделие не в буст-кампании по отчёту буста',
  ad_spend_not_collected: 'Расходы на рекламу не собираются',
  discount_base_not_known: 'Зачёркнутая цена не собрана',
  offer_not_published: 'Предложение не опубликовано на Маркете',
  metal_unknown: 'Металл не указан: порога нет, в вид «ниже порога» изделие не попадает',
  cheap_threshold_not_positive: 'Порог дешёвой витрины для изделия не больше нуля',
  cheap_threshold_not_computed: 'Порог дешёвой витрины не рассчитан',
}

const unavailableReasonLookup: Record<string, string> = Object.fromEntries(
  [...publishedReasonLabelTables, displayReasonLabels].flatMap((table) => Object.entries(table)),
)

/**
 * A raw code never reaches the screen. An operator who reads
 * "requires review" learns that something needs looking at; one who reads
 * `weight.package_only_forbidden` learns an engineering token. The code stays
 * available to a developer on the element's `data-reason` attribute.
 */
export const UNRESOLVED_LABEL = 'Причина недоступности требует проверки'

export function unavailableReasonText(code: string): string {
  return unavailableReasonLookup[code] ?? UNRESOLVED_LABEL
}

const providerDisplayNames: Record<string, string> = {
  'Яндекс Маркет': 'Яндекс Маркет',
  Ozon: 'Ozon',
  '1С': '1С',
  Wildberries: 'Wildberries',
}

export const WAREHOUSE_SOURCE_OPTIONS = [
  { value: 'Яндекс Маркет', label: 'Яндекс Маркет' },
  { value: 'Ozon', label: 'Ozon' },
  { value: '1С', label: '1С' },
  { value: 'Wildberries', label: 'Wildberries' },
] as const satisfies readonly { value: WarehouseSource; label: string }[]

export function providerLabel(providerCode: string): string {
  return providerDisplayNames[providerCode] ?? UNRESOLVED_LABEL
}

/** The Warehouse filter carries operator values, while the UI remains Russian. */
export const warehouseProductTypeLabels: Record<WarehouseProductTypeCode, string> = {
  ring: 'Кольца',
  earrings: 'Серьги',
  pendant: 'Подвески',
  chain: 'Цепи',
  bracelet: 'Браслеты',
  necklace: 'Колье',
  brooch: 'Броши',
  piercing: 'Пирсинг',
  wedding_ring: 'Обручальные кольца',
  cufflinks: 'Запонки',
}

const legacyProductTypeLabels: Record<string, string> = {
  'кольцо': 'Кольца', 'кольца': 'Кольца', 'серьги': 'Серьги',
  'подвеска': 'Подвески', 'подвески': 'Подвески', 'цепь': 'Цепи', 'цепи': 'Цепи',
  'цепочка': 'Цепи', 'цепочки': 'Цепи', 'браслет': 'Браслеты', 'браслеты': 'Браслеты',
  'колье': 'Колье', 'бусы': 'Колье', 'брошь': 'Броши', 'броши': 'Броши',
  'пирсинг': 'Пирсинг', 'обручальное кольцо': 'Обручальные кольца',
  'запонки': 'Запонки',
}

/** 1C writes this when a nomenclature card names no recognizable kind. */
export const UNSPECIFIED_PRODUCT_TYPE = 'unspecified'

/** Every stored type has a Russian name; an unmapped code is shown as itself, never hidden. */
export function productTypeLabel(productTypeCode: string): string {
  const knownLabel = Object.entries(warehouseProductTypeLabels).find(([code]) => code === productTypeCode)?.[1]
  if (knownLabel !== undefined) return knownLabel
  if (productTypeCode === UNSPECIFIED_PRODUCT_TYPE) return 'Не указан (1С)'
  return legacyProductTypeLabels[productTypeCode] ?? `Другое: ${productTypeCode}`
}

/** A member the browser has no sentence for is named in Russian, never echoed. */
export function fallbackLabel(): string {
  return UNRESOLVED_LABEL
}

export function labelFor<Key extends string>(table: Record<Key, string>, code: string): string {
  return code in table ? table[code as Key] : UNRESOLVED_LABEL
}

export function warehouseDateTime(value: string | null | undefined): string {
  if (!value) return 'Нет данных'
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}

const materialLabels: Record<string, string> = {
  gold: 'Золото', silver: 'Серебро', platinum: 'Платина', palladium: 'Палладий',
  'золото': 'Золото', 'серебро': 'Серебро', 'платина': 'Платина', 'палладий': 'Палладий',
}

const shapeLabels: Record<string, string> = {
  round: 'Круглая', oval: 'Овальная', square: 'Квадратная', rectangle: 'Прямоугольная',
  heart: 'Сердце', drop: 'Капля', marquise: 'Маркиз', cushion: 'Кушон', princess: 'Принцесса',
}

/** A stored code the browser has no word for stays visible, but as «Другое», never as a bare code. */
function ownerWord(table: Record<string, string>, value: string): string {
  const known = table[value.toLowerCase()]
  if (known !== undefined) return known
  if (/[а-яё]/i.test(value)) return value.charAt(0).toUpperCase() + value.slice(1)
  return `Другое: ${value}`
}

export function materialLabel(value: string): string {
  return ownerWord(materialLabels, value)
}

export function shapeLabel(value: string): string {
  return ownerWord(shapeLabels, value)
}

export function fineness(value: string): string {
  return /^\d+$/.test(value) ? `${value} проба` : value
}

const storefrontLabels: Record<string, string> = {
  wildberries: 'Wildberries',
  ymarket: 'Яндекс Маркет',
  ozon: 'Ozon',
  ori_site: 'ORI',
  ori: 'ORI',
}

const accountProviderLabels: Record<string, string> = { ...storefrontLabels, onec: '1С' }

/** A storefront account such as `ymarket:primary`, named by its storefront. */
export function accountCodeLabel(code: string): string {
  return accountProviderLabels[code.split(':')[0] ?? ''] ?? 'Другая площадка'
}

/** One Russian name per storefront code, used by the picker, the chips and the storefront marks. */
export function storefrontLabel(code: string): string {
  return storefrontLabels[code] ?? `Другое: ${code}`
}
